<template>
  <Teleport to="body">
    <div class="player-tour" :class="{ 'has-target': rect }">
      <!-- 高亮区域四周的遮罩：挡住其余点击，只留目标可点 -->
      <template v-if="rect">
        <div class="tour-shade" :style="shadeStyles.top"></div>
        <div class="tour-shade" :style="shadeStyles.bottom"></div>
        <div class="tour-shade" :style="shadeStyles.left"></div>
        <div class="tour-shade" :style="shadeStyles.right"></div>
        <div class="tour-ring" :style="holeStyle" aria-hidden="true"></div>
      </template>
      <div v-else class="tour-shade tour-shade--full"></div>

      <section
        ref="card"
        class="tour-card"
        role="dialog"
        aria-live="polite"
        :aria-label="step.title"
        :style="cardStyle"
      >
        <header class="tour-card-head">
          <span class="tour-progress">{{ index + 1 }} / {{ total }}</span>
          <button class="tour-skip" type="button" @click="$emit('skip')">{{ skipLabel }}</button>
        </header>
        <h3>{{ step.title }}</h3>
        <p>{{ step.body }}</p>
        <footer v-if="primaryLabel" class="tour-card-actions">
          <button
            v-if="step.secondaryLabel"
            class="tour-btn tour-btn--secondary"
            type="button"
            @click="$emit('secondary')"
          >
            {{ step.secondaryLabel }}
          </button>
          <button
            ref="primary"
            class="tour-btn tour-btn--primary"
            type="button"
            @click="$emit('primary')"
          >
            {{ primaryLabel }}
          </button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<script>
const HOLE_PADDING = 6
const CARD_GAP = 14
const VIEWPORT_MARGIN = 16

export default {
  name: 'PlayerTour',
  emits: ['primary', 'secondary', 'skip'],
  props: {
    // { title, body, target, waitsForAction, primaryLabel, secondaryLabel }
    // target 可以是多个选择器，高亮区取它们的并集；展开的下拉面板也算进去，这样能直接点
    step: { type: Object, required: true },
    index: { type: Number, required: true },
    total: { type: Number, required: true },
    skipLabel: { type: String, required: true },
  },
  data() {
    return {
      rect: null,
      cardSize: { width: 0, height: 0 },
      viewport: { width: window.innerWidth, height: window.innerHeight },
      frame: null,
    }
  },
  computed: {
    // 要用户亲手操作的步骤不给按钮，只能去点高亮处；目标不在页面上（如窄屏收进菜单）时由卡片按钮代劳
    primaryLabel() {
      return this.step.waitsForAction && this.rect ? '' : this.step.primaryLabel
    },
    holeStyle() {
      const { top, left, width, height } = this.rect
      return { top: `${top}px`, left: `${left}px`, width: `${width}px`, height: `${height}px` }
    },
    shadeStyles() {
      const { top, left, width, height } = this.rect
      return {
        top: { top: 0, left: 0, right: 0, height: `${top}px` },
        bottom: { top: `${top + height}px`, left: 0, right: 0, bottom: 0 },
        left: { top: `${top}px`, left: 0, width: `${left}px`, height: `${height}px` },
        right: { top: `${top}px`, left: `${left + width}px`, right: 0, height: `${height}px` },
      }
    },
    cardStyle() {
      const { width: cardWidth, height: cardHeight } = this.cardSize
      const { width: viewWidth, height: viewHeight } = this.viewport
      if (!this.rect) {
        return {
          top: `${Math.max(VIEWPORT_MARGIN, (viewHeight - cardHeight) / 2)}px`,
          left: `${Math.max(VIEWPORT_MARGIN, (viewWidth - cardWidth) / 2)}px`,
        }
      }
      // 依次试下、上、右、左，选第一个放得下且不压住高亮区的位置
      const { top, left, width, height } = this.rect
      const clampTop = (value) => Math.min(Math.max(VIEWPORT_MARGIN, value), viewHeight - cardHeight - VIEWPORT_MARGIN)
      const clampLeft = (value) => Math.min(Math.max(VIEWPORT_MARGIN, value), viewWidth - cardWidth - VIEWPORT_MARGIN)
      const centerTop = clampTop(top + height / 2 - cardHeight / 2)
      const centerLeft = clampLeft(left + width / 2 - cardWidth / 2)
      const candidates = [
        { top: top + height + CARD_GAP, left: centerLeft, space: viewHeight - (top + height) },
        { top: top - CARD_GAP - cardHeight, left: centerLeft, space: top },
        { top: centerTop, left: left + width + CARD_GAP, space: viewWidth - (left + width) },
        { top: centerTop, left: left - CARD_GAP - cardWidth, space: left },
      ]
      const fits = (spot) => spot.top >= VIEWPORT_MARGIN &&
        spot.top + cardHeight <= viewHeight - VIEWPORT_MARGIN &&
        spot.left >= VIEWPORT_MARGIN &&
        spot.left + cardWidth <= viewWidth - VIEWPORT_MARGIN
      const spot = candidates.find(fits) || candidates.reduce((best, item) => (item.space > best.space ? item : best))
      return { top: `${clampTop(spot.top)}px`, left: `${clampLeft(spot.left)}px` }
    },
  },
  watch: {
    step() {
      this.$nextTick(() => {
        this.measure()
        this.$refs.primary?.focus()
      })
    },
  },
  mounted() {
    // 播放器布局会随跟读栏出现、字幕滚动而变化，逐帧跟住目标位置
    const track = () => {
      this.measure()
      this.frame = requestAnimationFrame(track)
    }
    track()
    this.$nextTick(() => this.$refs.primary?.focus())
  },
  beforeUnmount() {
    cancelAnimationFrame(this.frame)
  },
  methods: {
    measure() {
      const boxes = Array.from(this.step.target ? document.querySelectorAll(this.step.target) : [])
        .map((element) => element.getBoundingClientRect())
        .filter((box) => box.width && box.height)
      let next = null
      if (boxes.length) {
        const top = Math.round(Math.min(...boxes.map((box) => box.top))) - HOLE_PADDING
        const left = Math.round(Math.min(...boxes.map((box) => box.left))) - HOLE_PADDING
        const bottom = Math.round(Math.max(...boxes.map((box) => box.bottom))) + HOLE_PADDING
        const right = Math.round(Math.max(...boxes.map((box) => box.right))) + HOLE_PADDING
        next = { top, left, width: right - left, height: bottom - top }
      }
      if (JSON.stringify(next) !== JSON.stringify(this.rect)) this.rect = next

      const card = this.$refs.card
      if (card.offsetWidth !== this.cardSize.width || card.offsetHeight !== this.cardSize.height) {
        this.cardSize = { width: card.offsetWidth, height: card.offsetHeight }
      }
      if (window.innerWidth !== this.viewport.width || window.innerHeight !== this.viewport.height) {
        this.viewport = { width: window.innerWidth, height: window.innerHeight }
      }
    },
  },
}
</script>

<style scoped>
.player-tour {
  position: fixed;
  inset: 0;
  z-index: 3000;
  pointer-events: none;
}

.tour-shade {
  position: fixed;
  background: var(--color-overlay);
  pointer-events: auto;
}

.tour-shade--full {
  inset: 0;
}

.tour-ring {
  position: fixed;
  border-radius: var(--radius-md);
  box-shadow: 0 0 0 2px var(--surface-panel);
}

.tour-card {
  position: fixed;
  width: min(340px, calc(100vw - 32px));
  padding: var(--space-4) var(--space-5) var(--space-5);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background: var(--surface-panel);
  box-shadow: var(--shadow-float);
  pointer-events: auto;
}

.tour-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-2);
}

.tour-progress {
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
  font-variant-numeric: tabular-nums;
}

.tour-skip {
  padding: var(--space-1) 0;
  border: none;
  background: none;
  color: var(--ink-secondary);
  font: inherit;
  font-size: var(--font-size-xs);
  cursor: pointer;
}

.tour-skip:hover {
  color: var(--ink-primary);
}

.tour-card h3 {
  margin: 0;
  color: var(--ink-primary);
  font-size: var(--font-size-md);
  font-weight: 600;
  line-height: 1.35;
}

.tour-card p {
  margin: var(--space-2) 0 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
  white-space: pre-line;
}

.tour-card-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  margin-top: var(--space-4);
}

.tour-btn {
  height: 36px;
  padding: 0 var(--space-4);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
}

.tour-btn--primary {
  border: 1px solid var(--ink-primary);
  color: var(--color-text-inverse);
  background: var(--ink-primary);
}

.tour-btn--secondary {
  border: 1px solid var(--border-default);
  color: var(--ink-primary);
  background: var(--surface-panel);
}

.tour-btn:focus-visible,
.tour-skip:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}
</style>
